import type { AnimationClip, BufferGeometry, Group, Material, Object3D } from 'three/webgpu';

import { detectQuality } from '../scenes/studio';
import type { ModelEntry } from '../assets/models';

interface MeshLike extends Object3D {
  readonly isMesh: true;
  geometry: BufferGeometry;
  material: Material | Material[];
}

function isMesh(obj: Object3D): obj is MeshLike {
  return (obj as { isMesh?: boolean }).isMesh === true;
}

function isSkinnedMesh(obj: Object3D): boolean {
  return (obj as { isSkinnedMesh?: boolean }).isSkinnedMesh === true;
}

export class ModelLoadError extends Error {
  constructor(
    public readonly code: 'TIER_BLOCKED' | 'NETWORK' | 'PARSE',
    message: string,
  ) {
    super(message);
    this.name = 'ModelLoadError';
  }
}

export interface LoadedModel {
  /**
   * Canonical source scene. Callers SHOULD clone (`source.clone(true)`)
   * before adding to a live scene so disposing one placement doesn't
   * affect others. Cached across loads of the same `ModelEntry`.
   */
  readonly source: Group;
  readonly animations: readonly AnimationClip[];
  dispose(): void;
}

type GLTFLoaderModule = typeof import('three/addons/loaders/GLTFLoader.js');

let loaderModulePromise: Promise<GLTFLoaderModule> | null = null;
const cache = new Map<string, Promise<LoadedModel>>();

async function getLoaderClass(): Promise<GLTFLoaderModule['GLTFLoader']> {
  if (!loaderModulePromise) {
    loaderModulePromise = import('three/addons/loaders/GLTFLoader.js');
  }
  return (await loaderModulePromise).GLTFLoader;
}

export async function loadModel(entry: ModelEntry): Promise<LoadedModel> {
  if (entry.tier === 'desktop-only' && detectQuality() === 'mobile') {
    throw new ModelLoadError('TIER_BLOCKED', `model '${entry.id}' is desktop-only`);
  }

  const cached = cache.get(entry.id);
  if (cached) return cached;

  const promise = (async (): Promise<LoadedModel> => {
    const GLTFLoader = await getLoaderClass();
    const loader = new GLTFLoader();
    let gltf;
    try {
      gltf = await loader.loadAsync(entry.path);
    } catch (err) {
      cache.delete(entry.id);
      throw new ModelLoadError(
        'NETWORK',
        `failed to fetch '${entry.path}': ${err instanceof Error ? err.message : String(err)}`,
      );
    }

    if (import.meta.env.DEV) warnOnSkinned(gltf.scene, entry.id);

    const animations = gltf.animations.slice();
    const source = gltf.scene as unknown as Group;

    return {
      source,
      animations,
      dispose: () => disposeScene(source, entry.id),
    };
  })();

  cache.set(entry.id, promise);
  return promise;
}

export function applyTransform(target: { position: { set(x: number, y: number, z: number): unknown }; rotation: { set(x: number, y: number, z: number): unknown }; scale: { set(x: number, y: number, z: number): unknown } }, defaults: ModelEntry['defaults']): void {
  if (!defaults) return;
  if (defaults.position) target.position.set(defaults.position[0], defaults.position[1], defaults.position[2]);
  if (defaults.rotation) target.rotation.set(defaults.rotation[0], defaults.rotation[1], defaults.rotation[2]);
  if (defaults.scale != null) {
    if (typeof defaults.scale === 'number') target.scale.set(defaults.scale, defaults.scale, defaults.scale);
    else target.scale.set(defaults.scale[0], defaults.scale[1], defaults.scale[2]);
  }
}

export function disposeAll(): void {
  for (const [, promise] of cache) {
    promise.then(loaded => loaded.dispose()).catch(() => { /* already failed */ });
  }
  cache.clear();
}

function warnOnSkinned(root: Group, id: string): void {
  let skinnedFound = 0;
  root.traverse(obj => {
    if (isSkinnedMesh(obj)) skinnedFound++;
  });
  if (skinnedFound > 0) {
    // eslint-disable-next-line no-console
    console.warn(
      `[modelLoader] '${id}' contains ${skinnedFound} SkinnedMesh — project policy is no-rig. ` +
      `Re-export from Blender with Include → Skinning OFF.`,
    );
  }
}

function disposeScene(root: Group, id: string): void {
  root.traverse(obj => {
    if (isMesh(obj)) {
      obj.geometry.dispose();
      const material = obj.material;
      if (Array.isArray(material)) material.forEach(m => m.dispose());
      else material.dispose();
    }
  });
  cache.delete(id);
}
